import React, { FormEvent, useEffect, useState } from 'react';
import sellerAccountService from '../services/sellerAccountService';
import { useAppContext } from '../context/AppContext';
import { useReferenceDataContext } from '../context/ReferenceDataContext';
import countryDialCodesList from "../data/countryDialCodes.json";

interface ProfileFormErrors {
    firstName?: string;
    lastName?: string;
    contactNumber?: string;
    countryCode?: string;
}

const MyAccountPage = () => {

    const { sellerAccount, setSellerAccount } = useAppContext();
    const { timezones } = useReferenceDataContext();

    const [firstName, setFirstName] = useState(sellerAccount?.firstname);
    const [lastName, setLastName] = useState(sellerAccount?.lastname);
    const [contactNumber, setContactNumber] = useState(sellerAccount?.contactNumber);
    const [timezone, setTimezone] = useState(sellerAccount?.timezone);
    const [countryCode, setCountryCode] = useState(sellerAccount?.countryCode || "");

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [profileFormErrors, setProfileFormErrors] = useState<ProfileFormErrors>({});
    const [profileServiceStatus, setProfileServiceStatus] = useState({ type: '', message: '' });
    const [emailServiceStatus, setEmailServiceStatus] = useState({ type: '', message: '' });

    const [verifyCooldown, setVerifyCooldown] = useState(false);
    const [cooldownSeconds, setCooldownSeconds] = useState(30);

    const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
    const [countryDialCodes] = useState(countryDialCodesList); // Assume countryDialCodesList is imported or defined elsewhere

    const validateProfileChange = () => {
        let errors: ProfileFormErrors = {};
        if (!firstName) {
            errors.firstName = "Please enter first name";
        }
        if (!lastName) {
            errors.lastName = "Please enter last name";
        }
        if (!contactNumber) {
            errors.contactNumber = "Please enter contact number";
        }

        if (!countryCode) {
            errors.countryCode = "Country code is required";
        }
        setProfileFormErrors(errors);
        return Object.keys(errors).length === 0;
    }

    const handleProfileSubmit = (e: FormEvent) => {
        const updateSellerAccountProfile = async () => {
            try {
                const result = await sellerAccountService.updateSellerAccountProfile(firstName!, lastName!, contactNumber!, timezone!, countryCode!);
                if (result === 'Success') {
                    setSellerAccount({ ...sellerAccount!, firstname: firstName!, lastname: lastName!, contactNumber: contactNumber!, timezone: timezone! });
                    setProfileServiceStatus({ ...profileServiceStatus, type: "Success", message: "The account profile has been updated successfully" });
                }
                else {
                    setProfileServiceStatus({ ...profileServiceStatus, type: "Error", message: "Failed to update account profile" });
                }
            } catch (error) {
                setProfileServiceStatus({ ...profileServiceStatus, type: "Error", message: "Failed to update account profile" });
            }
            setIsSubmitting(false);
        }
        e.preventDefault();
        setProfileServiceStatus({ ...profileServiceStatus, type: "", message: "" });
        if (validateProfileChange()) {
            setIsSubmitting(true);
            updateSellerAccountProfile();
        }
    };



    const handleVerifyEmail = async () => {
        setEmailServiceStatus({ ...emailServiceStatus, type: "", message: "" });
        setIsVerifyingEmail(true); // start spinner

        try {
            const result = await sellerAccountService.sendEmailVerification();

            if (result === "Success") {
                setEmailServiceStatus({
                    ...emailServiceStatus,
                    type: "Success",
                    message: "A verification email has been sent to your registered email address, please check your inbox."
                });

                // Start cooldown
                setVerifyCooldown(true);
                setCooldownSeconds(30);
            } else if (result === "TooFrequent") {
                setEmailServiceStatus({
                    ...emailServiceStatus,
                    type: "Error",
                    message: "You have requested for email verification too frequently, please try again in 3 mins later."
                });
            } else {
                setEmailServiceStatus({
                    ...emailServiceStatus,
                    type: "Error",
                    message: "Failed to send verification email."
                });
            }
        } catch (error) {
            setEmailServiceStatus({
                ...emailServiceStatus,
                type: "Error",
                message: "Failed to send verification email."
            });
        } finally {
            setIsVerifyingEmail(false); // stop spinner
        }
    };


    // Countdown effect
    useEffect(() => {
        if (!verifyCooldown) return;

        const timer = setInterval(() => {
            setCooldownSeconds(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    setVerifyCooldown(false);
                    return 30; // reset for next time
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [verifyCooldown]);

    return (
        <div className='mt-3'>
            <div className="card rounded-0">
                <div className="card-body card-body p-4">
                    <h5 className="card-title mb-4">My Account</h5>
                    <form className="row g-3" onSubmit={handleProfileSubmit}>
                        <div className="col-12 mb-3">
                            <label className="form-label">Username</label>
                            <div className="d-flex align-items-center gap-3">
                                <p className="mb-0">{sellerAccount?.username}</p>
                                {sellerAccount?.emailVerified ? (
                                    <span className="badge bg-success">Verified</span>
                                ) : (
                                    <>
                                        <span className="badge bg-danger">Unverified</span>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-dark rounded-0"
                                            onClick={handleVerifyEmail}
                                            disabled={verifyCooldown || isVerifyingEmail}
                                        >
                                            {isVerifyingEmail ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Sending...
                                                </>
                                            ) : verifyCooldown ? `Resend in ${cooldownSeconds}s` : "Verify Now"}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                        {emailServiceStatus.type === "Success" && <div className="col-md-12 mt-0 mb-2 clearfix"><div className="alert alert-success p-2 mb-0">{emailServiceStatus.message}</div></div>}
                        {emailServiceStatus.type === "Error" && <div className="col-md-12 mt-0 mb-2 clearfix"><div className="alert alert-danger p-2 mb-0">{emailServiceStatus.message}</div></div>}
                        <div className="col-6">
                            <label className="form-label">First Name</label>
                            <input
                                type="text"
                                className={`form-control ${profileFormErrors.firstName ? ` is-invalid` : ``}`}
                                maxLength={200}
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                            />
                            {profileFormErrors.firstName && <div className="invalid-feedback">{profileFormErrors.firstName}</div>}
                        </div>
                        <div className="col-6">
                            <label className="form-label">Last Name</label>
                            <input
                                type="text"
                                className={`form-control ${profileFormErrors.lastName ? ` is-invalid` : ``}`}
                                value={lastName}
                                maxLength={200}
                                onChange={(e) => setLastName(e.target.value)}
                            />
                            {profileFormErrors.lastName && <div className="invalid-feedback">{profileFormErrors.lastName}</div>}
                        </div>
                        <div className="col-6">
                            <div className="d-flex align-items-center gap-2 mb-1">
                                <div className='col-2 me-2'>
                                    <label htmlFor="inputCountryCode" className="form-label">Country Code</label>
                                    <div className="dropdown">
                                        <button
                                            className="btn btn-outline-dark dropdown-toggle  w-100 "
                                            type="button"
                                            data-bs-toggle="dropdown"
                                        >
                                            {countryCode || "Select"}
                                        </button>
                                        <ul className="dropdown-menu" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                            {countryDialCodes.map(c => (
                                                <li key={c.code}>
                                                    <button
                                                        className="dropdown-item"
                                                        type="button"
                                                        onClick={() => setCountryCode("+" + c.code)}
                                                    >
                                                        {c.country} +{c.code}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    {profileFormErrors.countryCode && <div className="invalid-feedback">{profileFormErrors.countryCode}</div>}

                                </div>
                                <div className='col-9'>
                                    <label className="form-label">Contact Number</label>
                                    <input
                                        type="text"
                                        className={`form-control ${profileFormErrors.contactNumber ? ` is-invalid` : ``}`}
                                        maxLength={200}
                                        value={contactNumber}
                                        onChange={(e) => setContactNumber(e.target.value)}
                                    />
                                    {profileFormErrors.contactNumber && <div className="invalid-feedback">{profileFormErrors.contactNumber}</div>}
                                </div>
                            </div>

                        </div>
                        <div className="col-6">
                            <label className="form-label">Timezone</label>
                            <select
                                className={`form-select`}
                                value={timezone}
                                onChange={(e) => setTimezone(e.target.value)}>
                                {timezones && timezones.map((tz, index) => (
                                    <option key={index} value={tz.timezoneId}>
                                        {tz.displayName}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {profileServiceStatus.type === "Success" && <div className="col-md-12 clearfix"><div className="alert alert-success p-2 mb-0">{profileServiceStatus.message}</div></div>}
                        {profileServiceStatus.type === "Error" && <div className="col-md-12 clearfix"><div className="alert alert-danger p-2 mb-0">{profileServiceStatus.message}</div></div>}
                        <div className="col-md-12 pt-2">
                            <button type="submit" className="btn btn-dark rounded-0" disabled={isSubmitting}>
                                {isSubmitting ? (<><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Saving ...</>) : ("Save")}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default MyAccountPage;